import { observer } from 'mobx-react-lite'
import { useLayoutEffect, useRef, useState } from 'react'
import { BreadcrumbsUi, Button, FullPageWrapper, Tooltip, Typography } from 'shared/ui'
import { type IDefaultEmptyStateProps } from 'shared/ui/EmptyState'
import { NoResultsFound } from 'shared/ui/NoResultsFound/NoResultsFound'
import { Table, TableSearch } from 'shared/ui/TableNew'
import { uiStore } from 'shared/store/uiStore'
import { type Team } from 'entities/Teams'
import { TeamsPath } from 'pages/settings/pages/teams/route/teamsPath'
import { useTeamsContext } from 'pages/settings/pages/teams/context/teamsContext'
import { type MemberRow } from 'pages/settings/pages/teams/ui/ManageTeamMembers/MemberRow'

import styles from './styles.module.scss'

type IManageTeamMembersProps = {
  team: Team
}

export const ManageTeamMembers = observer(({ team }: IManageTeamMembersProps) => {
  const teamsStore = useTeamsContext()
  const { name, description, origin } = team
  const teamId = origin.id

  const {
    membersTableColumns,
    membersTableStore,
    getIsAllMembersAdded,
    getMembersRows,
    openDeleteModal,
    openAddMembersModal,
    handleRemoveMembers,
    handleRequestRenameTeam,
    handleResyncIntegrationTeam,
    getResyncingIntegrationTeam,
    getSortedAndFilteredMembersRows,
  } = teamsStore
  const membersRows = getMembersRows(team)
  const isAllMembersAdded = getIsAllMembersAdded(team)
  const isResyncing = getResyncingIntegrationTeam(teamId)
  const [membersSearch, setMembersSearch] = useState('')
  const descriptionRef = useRef<HTMLSpanElement>(null)
  const [isDescriptionTruncated, setIsDescriptionTruncated] = useState(false)

  useLayoutEffect(() => {
    const el = descriptionRef.current
    if (!el || !description) {
      setIsDescriptionTruncated(false)
      return
    }
    setIsDescriptionTruncated(el.scrollHeight > el.clientHeight)
  }, [description])
  const filteredMembersRows = getSortedAndFilteredMembersRows(membersRows, membersSearch)
  const selectedCount = membersTableStore.bulkStore.selectedIds.length
  return (
    <FullPageWrapper className={styles.page}>
      <div className={styles.content}>
        <div className={styles.backRow}>
          <BreadcrumbsUi path={TeamsPath.root} label='Teams' />
        </div>
        <div className={styles.header}>
          <div className={styles.headerTitleBlock}>
            <Typography
              className={styles.pageTitle}
              variant='heading-lg'
              ariaLabel='ManageTeamMembersTitle'
            >
              {name}
            </Typography>
            {description && (
              <div className={styles.pageDescriptionWrap}>
                {isDescriptionTruncated ? (
                  <Tooltip label={description} ariaLabel='ManageTeamMembersDescription'>
                    <span ref={descriptionRef} className={styles.pageDescription}>
                      {description}
                    </span>
                  </Tooltip>
                ) : (
                  <span ref={descriptionRef} className={styles.pageDescription}>
                    {description}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className={styles.headerActions}>
            {!team.isIntegrationTeam && (
              <Button
                text='Rename'
                typeBtn='contained'
                contained='secondary'
                size='medium'
                icon='edit'
                onClick={() => handleRequestRenameTeam(team)}
                aria-label={'ManageTeamMembersRename'}
              />
            )}
            <Button
              text='Delete'
              typeBtn='contained'
              contained='secondary'
              size='medium'
              icon='delete'
              intent='destructive'
              onClick={() =>
                openDeleteModal(teamId, team.isIntegrationTeam, () =>
                  uiStore.changeRoute({ path: TeamsPath.root })
                )
              }
              aria-label={'ManageTeamMembersDelete'}
            />
          </div>
        </div>

        <div className={styles.membersSection}>
          <div className={styles.membersSectionHeader}>
            <Typography
              className={styles.pageTitle}
              variant='heading-sm'
              ariaLabel='ManageTeamMembersMembersTitle'
            >
              Members
            </Typography>
            <div className={styles.membersActions}>
              {!team.isIntegrationTeam && (
                <Button
                  text='Add members'
                  typeBtn='contained'
                  contained='primary'
                  size='medium'
                  icon='add'
                  disabled={isAllMembersAdded}
                  tooltipProps={
                    isAllMembersAdded
                      ? { label: 'You have added all members to this team', placement: 'top' }
                      : undefined
                  }
                  onClick={() => openAddMembersModal(team)}
                  aria-label={'ManageTeamMembersAddMembers'}
                />
              )}
              {team.isIntegrationTeam && (
                <Button
                  text='Refresh data'
                  typeBtn='contained'
                  contained='secondary'
                  size='medium'
                  icon='refresh'
                  loading={isResyncing}
                  disabled={isResyncing}
                  onClick={() => handleResyncIntegrationTeam(teamId, true)}
                  aria-label={'ManageTeamMembersResyncData'}
                />
              )}
            </div>
          </div>

          <div className={styles.membersTableWrap}>
            <TableSearch
              value={membersSearch}
              onChange={setMembersSearch}
              className={styles.membersSearch}
            />
            {membersSearch.trim() && filteredMembersRows.length === 0 ? (
              <div className={styles.noResultsWrap}>
                <NoResultsFound label='No results found' />
              </div>
            ) : (
              <Table<MemberRow>
                store={membersTableStore}
                rows={filteredMembersRows}
                columns={membersTableColumns}
                loading={false}
                hidePagination
                withCheckbox={!team.isIntegrationTeam}
                withSelectAll={!team.isIntegrationTeam}
                withoutSelectedCount={selectedCount > 0}
                ariaLabel='ManageTeamMembersTable'
                headerLeftContentProps={
                  !team.isIntegrationTeam
                    ? (() => {
                        const isAllMembersSelected = selectedCount >= team.origin.members.length
                        return {
                          actionsContent: (
                            <Button
                              text={selectedCount > 1 ? 'Remove members' : 'Remove member'}
                              icon='delete'
                              size='small'
                              contained='tertiary'
                              disabled={isAllMembersSelected}
                              className={styles.removeButton}
                              tooltipProps={
                                isAllMembersSelected
                                  ? {
                                      type: 'description',
                                      label: "Can't remove all members",
                                      desc: 'Teams must have at least one member',
                                      placement: 'top',
                                    }
                                  : undefined
                              }
                              onClick={() =>
                                handleRemoveMembers(
                                  teamId,
                                  membersTableStore.bulkStore.selectedIds as number[]
                                )
                              }
                              aria-label={'ManageTeamMembersRemoveMembers'}
                            />
                          ),
                        }
                      })()
                    : undefined
                }
                emptyStateProps={
                  {
                    title: 'No members',
                    text: 'Add members to this team to get started.',
                    iconProps: { icon: 'user' },
                  } satisfies IDefaultEmptyStateProps
                }
              />
            )}
          </div>
        </div>
      </div>
    </FullPageWrapper>
  )
})
